import classnames from 'classnames';
import React from 'react';

import {Icon, IconButton, Text} from '@albert-io/atomic';

import {HIGHLIGHT_BACKGROUND_CLASS} from './annotationHighlightStyles';
import type {QuestionNote} from './annotationNotes';
import {useQuestionNotes} from './useQuestionNotes';

interface AnnotationNotesPanelListProps {
  questionId: string | undefined;
  /** Opens a note for editing (#20497). Without it the pencil is inert. */
  onEditNote?: (note: QuestionNote) => void;
}

/**
 * The question's notes, each under the text it was written about. An underlined
 * mark is quoted underlined rather than filled, matching AnnotationNoteList.
 */
export default function AnnotationNotesPanelList({
  questionId,
  onEditNote
}: AnnotationNotesPanelListProps) {
  const notes = useQuestionNotes(questionId);

  if (notes.length === 0) {
    return (
      <div
        className='gap-x1-half px-x2 py-x5 flex flex-col items-center text-center'
        data-testid='annotation-notes-panel-empty'
      >
        {/* Important beats `.a-icon--size-inherit`'s own font-size, which ties on specificity. */}
        <Icon icon='notes' iconStyle='light' color='tertiary' className='text-[39px]!' />
        <Text size='m' color='tertiary'>
          Highlight items to make notes.
        </Text>
      </div>
    );
  }

  // m-x0: without preflight the UA's 1em list margins survive.
  return (
    <ul className='gap-x1-half m-x0 p-x2 flex list-none flex-col'>
      {notes.map((note) => {
        const {id, anchor, color, type} = note.annotation;
        return (
          <li
            key={id}
            className='rounded-x1-half gap-x1 p-x1-half background-primary border-border-primary flex flex-col border border-solid'
            data-testid='annotation-notes-panel-item'
          >
            <div className='flex items-center'>
              {/* min-w-0: `truncate` never engages on a flex child that can't shrink. */}
              <Text
                size='s'
                color='primary'
                className={classnames('rounded-x1 p-x0-half min-w-0 flex-1 truncate', {
                  [HIGHLIGHT_BACKGROUND_CLASS[color]]: type === 'highlight',
                  underline: type === 'underline'
                })}
              >
                {`“${anchor.exact}”`}
              </Text>
              <IconButton
                icon='pencil'
                iconStyle='regular'
                // Named per mark, or every note's pencil announces identically.
                label={`Edit note on “${anchor.exact}”`}
                size='xs'
                className='ml-x1 shrink-0'
                onClick={() => onEditNote?.(note)}
              />
            </div>
            {/* Notes merged from combined marks are newline-separated (annotationMerge). */}
            <Text size='s' color='primary' className='wrap-break-word whitespace-pre-wrap'>
              {note.annotation.note}
            </Text>
          </li>
        );
      })}
    </ul>
  );
}
